/**
 * AdminBulkReportsCard — lean MVP for the "Bulk Download & Analysis"
 * control mounted at the top of the Reports admin page.
 *
 * Lets an admin:
 *   1. Multi-select company codes (via the existing StockSearchDropdown +
 *      a chip row for already-picked tickers).
 *   2. Pick report types (Annual-only / Annual + Interim / All).
 *   3. Pick a retention window (1 / 3 / 5 / 10 years).
 *   4. Fire one of 3 actions:
 *        • 📥 Download Missing Only        — CSE fetch, NO Claude
 *        • ✨ Analyse Downloaded Only      — Claude on already-local
 *                                            pending reports ONLY
 *        • 📥✨ Download & Analyse All     — CSE fetch + Claude on
 *                                            the new PDFs
 *
 * Separates network cost from LLM cost so admins can spend tokens
 * deliberately. Wraps a single backend endpoint
 * (POST /api/admin/reports/bulk-action).
 *
 * NOTE: intentionally small. Filters by score / sector / analysis
 * status and Quick-Select presets (Top-10 BUY, etc.) are deferred to
 * a P2 follow-up per the user's Option B MVP scope.
 */
import React, { useState } from "react";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CloudDownload, Sparkles, Loader2, X } from "lucide-react";
import StockSearchDropdown from "@/components/common/StockSearchDropdown";

const TYPE_OPTIONS = [
  { value: "annual", label: "Annual only" },
  { value: "annual,interim", label: "Annual + Interim" },
  { value: "all", label: "All (including quarterly)" },
];

const YEAR_OPTIONS = [
  { value: "1", label: "Last 1 year" },
  { value: "3", label: "Last 3 years" },
  { value: "5", label: "Last 5 years" },
  { value: "10", label: "Last 10 years" },
  { value: "0", label: "Unlimited" },
];

const AdminBulkReportsCard = () => {
  // Selection state — array of {cse_symbol, company_code, company_name}
  // company_code is extracted from cse_symbol (e.g. "COMB.N0000" → "COMB").
  const [selected, setSelected] = useState([]);
  const [typeMode, setTypeMode] = useState("annual"); // annual | annual,interim | all
  const [yearsMode, setYearsMode] = useState("5");
  const [busy, setBusy] = useState(null); // "download" | "analyse" | "both" | null
  const [lastResult, setLastResult] = useState(null);

  const excludedSymbols = selected.map((s) => s.cse_symbol);

  const onAdd = (cse_symbol, security) => {
    if (!cse_symbol || !security) return;
    // Derive company_code. Prefer explicit field, else strip the .N0000 suffix.
    const companyCode = security.company_code || cse_symbol.split(".")[0];
    if (selected.some((s) => s.company_code === companyCode)) return;
    setSelected((prev) => [
      ...prev,
      {
        cse_symbol,
        company_code: companyCode,
        company_name: security.company_name || security.display_name || cse_symbol,
      },
    ]);
  };

  const removeOne = (code) => {
    setSelected((prev) => prev.filter((s) => s.company_code !== code));
  };

  const clearAll = () => setSelected([]);

  const buildPayload = (action) => {
    const report_types =
      typeMode === "all" ? null : typeMode.split(",").map((t) => t.trim()).filter(Boolean);
    const years = parseInt(yearsMode, 10);
    return {
      action,
      company_codes: selected.map((s) => s.company_code),
      report_types,
      years_to_keep: years > 0 ? years : null,
      limit_per_company: 10,
      analysis_limit: 60,
    };
  };

  const runAction = async (mode) => {
    if (selected.length === 0) {
      toast.error("Pick at least one company first.");
      return;
    }
    const actionMap = { download: "download", analyse: "analyse", both: "download_and_analyse" };
    const action = actionMap[mode];
    setBusy(mode);
    setLastResult(null);
    try {
      const res = await apiPost("/admin/reports/bulk-action", buildPayload(action));
      setLastResult({ mode, ...res });
      const downloaded = res?.downloaded ?? 0;
      const skipped = res?.skipped_existing ?? 0;
      const queued = res?.analysis_queued ?? 0;
      if (mode === "download") {
        toast.success(
          downloaded > 0
            ? `Downloaded ${downloaded} new report${downloaded === 1 ? "" : "s"} across ${selected.length} compan${selected.length === 1 ? "y" : "ies"}`
            : "No new reports found on cse.lk",
          { description: skipped ? `${skipped} already on file.` : "Analysis not triggered.", duration: 6000 }
        );
      } else if (mode === "analyse") {
        toast.success(
          queued > 0
            ? `Queued ${queued} report${queued === 1 ? "" : "s"} for Claude analysis`
            : "No pending reports to analyse",
          {
            description: queued > 0
              ? "Running in the background — refresh the library in ~1–2 min."
              : "Every report on file for the selected companies is already analysed.",
            duration: 6000,
          }
        );
      } else {
        toast.success(
          `Downloaded ${downloaded} · Analysing ${queued}`,
          { description: "Claude is processing the new PDFs in the background.", duration: 6000 }
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${mode} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border bg-card" data-testid="admin-bulk-reports-card">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-primary">
            <CloudDownload className="h-4 w-4" />
            <Sparkles className="h-4 w-4" />
          </span>
          Bulk Download &amp; Analysis
          <Badge variant="outline" className="text-[10px] ml-1">Admin · MVP</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">Download ≠ Analyse.</span>{" "}
          Downloading pulls PDFs from cse.lk (cheap). Analysing runs Claude on each
          report to extract EPS / revenue / margins (costs LLM tokens). Pick the
          tickers + filters below, then choose an action.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Row 1 — picker + filters */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px_180px] gap-3">
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Companies
            </label>
            <StockSearchDropdown
              value=""
              onChange={onAdd}
              excluded={excludedSymbols}
              placeholder="Add a ticker to the batch…"
              testId="bulk-admin-picker"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Report types
            </label>
            <Select value={typeMode} onValueChange={setTypeMode}>
              <SelectTrigger data-testid="bulk-admin-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`bulk-admin-type-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Retention
            </label>
            <Select value={yearsMode} onValueChange={setYearsMode}>
              <SelectTrigger data-testid="bulk-admin-years-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`bulk-admin-years-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 2 — selected chips */}
        <div className="min-h-[38px] flex items-center flex-wrap gap-1.5 rounded-md border border-dashed p-2" data-testid="bulk-admin-selected-chips">
          {selected.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              No companies selected — add at least one ticker above.
            </span>
          ) : (
            <>
              {selected.map((s) => (
                <Badge
                  key={s.company_code}
                  variant="secondary"
                  className="gap-1.5 pr-1"
                  data-testid={`bulk-admin-chip-${s.company_code}`}
                >
                  <span className="font-mono text-[10px]">{s.company_code}</span>
                  <span className="text-xs truncate max-w-[160px]">{s.company_name}</span>
                  <button
                    type="button"
                    onClick={() => removeOne(s.company_code)}
                    className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-foreground/10"
                    aria-label={`Remove ${s.company_code}`}
                    data-testid={`bulk-admin-chip-remove-${s.company_code}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="text-[11px] text-muted-foreground hover:text-destructive ml-auto"
                data-testid="bulk-admin-clear-all"
              >
                Clear all
              </button>
            </>
          )}
        </div>

        {/* Row 3 — actions */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-muted-foreground">
            {selected.length > 0 && (
              <>Acting on <span className="font-semibold text-foreground">{selected.length}</span> compan{selected.length === 1 ? "y" : "ies"}.</>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => runAction("download")}
              disabled={!!busy || selected.length === 0}
              className="gap-1.5"
              data-testid="bulk-admin-download-button"
              title="Fetch new reports from cse.lk for the selected companies. NO Claude analysis."
            >
              {busy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudDownload className="h-3.5 w-3.5" />}
              Download Missing Only
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runAction("analyse")}
              disabled={!!busy || selected.length === 0}
              className="gap-1.5"
              data-testid="bulk-admin-analyse-button"
              title="Run Claude on reports already on file (pending analysis). NO new downloads."
            >
              {busy === "analyse" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Analyse Downloaded Only
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => runAction("both")}
              disabled={!!busy || selected.length === 0}
              className="gap-1.5"
              data-testid="bulk-admin-download-and-analyse-button"
              title="Fetch new reports AND immediately run Claude on them."
            >
              {busy === "both" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                <span className="inline-flex items-center gap-0.5">
                  <CloudDownload className="h-3.5 w-3.5" />
                  <Sparkles className="h-3.5 w-3.5" />
                </span>
              )}
              Download &amp; Analyse All
            </Button>
          </div>
        </div>

        {/* Row 4 — last result summary */}
        {lastResult && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground" data-testid="bulk-admin-last-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px] capitalize">
                {lastResult.mode === "both" ? "Download & Analyse" : lastResult.mode}
              </Badge>
              <span>
                <span className="font-semibold text-foreground">{lastResult.company_count}</span> compan{lastResult.company_count === 1 ? "y" : "ies"}
                {" · "}
                <span className="font-semibold text-foreground">{lastResult.downloaded ?? 0}</span> downloaded
                {" · "}
                <span className="font-semibold text-foreground">{lastResult.skipped_existing ?? 0}</span> skipped
                {" · "}
                <span className="font-semibold text-foreground">{lastResult.analysis_queued ?? 0}</span> queued for Claude
                {lastResult.download_errors ? (
                  <span className="text-destructive">
                    {" · "}
                    {lastResult.download_errors} error{lastResult.download_errors === 1 ? "" : "s"}
                  </span>
                ) : null}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBulkReportsCard;
